import React, { useState } from "react";
import {
  Select,
  TextArea,
  MyMessage,
  InputField,
  PhoneInput,
  MyButton,
  AddPageShell,
  AddSection,
} from "../../components/Component";
import { useDoSeeDataContext } from "../../../context/DoSeeDataContext";
import { FOLDERS, image_kit_folder } from "../utils/Utils";
import { defaultImage } from "../../../lib/libraries/imageKit/ImageKit";
import { ImageKitUpload } from "../../../ui/ImageKitLibrary";
import { useGeoPositionContext } from "../utils/geoPosition";
import { _TYPES_ } from "../utils/types";
import GenerateDescription from "../utils/GenerateDescription";
import { FaHandSparkles } from "react-icons/fa";
import {
  FiImage,
  FiPhone,
  FiShare2,
  FiMapPin,
  FiSettings,
  FiAlignLeft,
  FiHome,
} from "react-icons/fi";

const Add = () => {
  const { createOneContent } = useDoSeeDataContext();
  const {
    country_id,
    entry_label,
    country_name,
    first_level_id,
    fourth_level_id,
    second_level_id,
    third_level_id,
    tags,
  } = useGeoPositionContext();

  const [thumbnail, setThumbnail] = useState(defaultImage);
  const [thumbnailId, setThumbnailId] = useState("img_cdn_id");
  const [error, setError] = useState(false);
  const [message, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [title, setTitle] = useState(null);
  const [email, setEmail] = useState(null);
  const [payLink, setPayLink] = useState(null);
  const [phoneNumber, setPhoneNumber] = useState(null);
  const [whatsapp, setWhatsapp] = useState("none");
  const [facebook, setFacebook] = useState("none");
  const [twitter, setTwitter] = useState("none");
  const [instagram, setInstagram] = useState("none");
  const [youtube, setYoutube] = useState("none");
  const [hashtags, setHashtags] = useState("");
  const [googleMapCode, setGoogleMapCode] = useState("none");
  const [latitude, setLatitude] = useState("none");
  const [longitude, setLongitude] = useState("none");
  const [formattedAddress, setFormattedAddress] = useState(null);
  const [expo, setExpo] = useState(0);
  const [featured, setFeatured] = useState<any>("no");
  const [promoted, setPromoted] = useState<any>("no");
  const [embassyStatus, setEmbassyStatus] = useState(1);
  const [description, setDescription] = useState(null);
  const embassyRoutes = "/data/embassy";
  const [showDescriptionModal, setShowDescriptionModal] = useState<any>(null);

  const cleanFields = () => {
    setLoading(false);
    setSuccess(true);
    setThumbnail(null);
    setThumbnailId(null);
    setTitle(null);
    setEmail(null);
    setPayLink(null);
    setExpo(0);
    setPhoneNumber(null);
    setWhatsapp("none");
    setFacebook("none");
    setTwitter("none");
    setInstagram("none");
    setYoutube("none");
    setHashtags("none");
    setGoogleMapCode("none");
    setLatitude("none");
    setLongitude("none");
    setFormattedAddress(null);
    setFeatured(0);
    setDescription(null);
  };

  const responses = (error: any, message: any) => {
    setError(error);
    setLoading(false);
    setSuccess(!error);
    setErrorMessage(message);
  };

  const addOneEmbassyHandler = async (e: any) => {
    e.preventDefault();
    setLoading(true);
    if (!thumbnail || !thumbnailId) {
      responses(true, "Image Required");
    } else {
      await sendDBRequest();
    }
  };

  const sendDBRequest = async () => {
    const data = {
      country_id: country_id,
      first_level_id: first_level_id ? first_level_id : 0,
      second_level_id: second_level_id ? second_level_id : 0,
      third_level_id: third_level_id ? third_level_id : 0,
      fourth_level_id: fourth_level_id ? fourth_level_id : 0,
      title: title,
      slug: title ? (title as any).toLowerCase().replace(/\s/g, "_") : null,
      description: description,
      thumbnail: thumbnail ? thumbnail : defaultImage,
      image_cdn_id: thumbnailId ? thumbnailId : "",
      phone_number: phoneNumber,
      email_address: email,
      pay_link: payLink,
      whatsapp: whatsapp,
      facebook: facebook,
      twitter: twitter,
      instagram: instagram,
      youtube: youtube,
      hashtag: `${tags}, ${hashtags}`,
      audio_adv: "none",
      google_map_code: googleMapCode,
      latitude: latitude,
      longitude: longitude,
      formatted_address: formattedAddress,
      country_short: "none",
      status: embassyStatus,
      featured: featured,
      promoted: promoted,
      expo: expo,
    };

    const { status, message } = await createOneContent({
      data: data,
      content: _TYPES_.EMBASSY,
    });
    if (status === 200 || status === 201) {
      cleanFields();
      responses(false, message);
    } else {
      responses(true, message);
    }
  };

  return (
    <AddPageShell
      backRoute={embassyRoutes}
      eyebrow="DoSee Data • Embassies"
      title="Add Embassy"
      subtitle={`Add a diplomatic mission for travellers in ${entry_label || "this destination"}.`}
      icon={<FiHome className="h-5 w-5" />}
      modals={
        showDescriptionModal ? (
          <GenerateDescription
            setDescription={setDescription}
            setShowDescriptionModal={setShowDescriptionModal}
          />
        ) : null
      }
      loading={loading}
      submitLabel="Add Embassy"
      onSubmit={addOneEmbassyHandler}
      onSubmitClick={() => {
        setError(false);
        setSuccess(false);
        setErrorMessage(null);
      }}
      message={
        <MyMessage
          error={error}
          message={message}
          success={success}
          onExpired={() => {
            setError(false);
            setSuccess(false);
          }}
          onClick={() => {
            setError(false);
            setSuccess(false);
            setErrorMessage("");
          }}
        />
      }
    >
      <AddSection
        title="Cover Image"
        hint="A clear photo of the embassy or its flag works well."
        icon={<FiImage className="h-4 w-4" />}
      >
        <ImageKitUpload
          thumbnail={thumbnail}
          thumbnailId={thumbnailId}
          setThumbnail={setThumbnail}
          setThumbnailId={setThumbnailId}
          folder={image_kit_folder(country_name, FOLDERS.EMBASSIES)}
        />
      </AddSection>

      <AddSection title="Basics" hint="Embassy name and address.">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          <InputField
            props={{
              id: "title",
              title: "Title",
              type: "text",
              required: true,
              value: title ? title : "",
              onChange: (e: any) => {
                setTitle(e.target.value);
                setError(null);
                setSuccess(null);
              },
            }}
          />
          <InputField
            props={{
              id: "formatted_address",
              title: "Formatted Address",
              type: "address",
              value: formattedAddress ? formattedAddress : "",
              onChange: (e: any) => {
                setFormattedAddress(e.target.value);
                setError(null);
                setSuccess(null);
              },
            }}
          />
          <InputField
            props={{
              id: "hashtag",
              title: "Tags (comma separated)",
              type: "text",
              value: hashtags ? hashtags : "",
              onChange: (e: any) => {
                setHashtags(e.target.value);
                setError(null);
                setSuccess(null);
              },
            }}
          />
        </div>
      </AddSection>

      <AddSection
        title="Contact"
        hint="How travellers can reach the embassy."
        icon={<FiPhone className="h-4 w-4" />}
      >
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          <PhoneInput
            setPhone={setPhoneNumber}
            phone={phoneNumber}
            required={false}
          />
          <InputField
            props={{
              id: "email",
              title: "Email Address",
              type: "email",
              value: email ? email : "",
              onChange: (e: any) => {
                setEmail(e.target.value);
                setError(null);
                setSuccess(null);
              },
            }}
          />
          <InputField
            props={{
              id: "pay-link",
              title: "Pay Link (Optional)",
              type: "text",
              value: payLink ? payLink : null,
              onChange: (e: any) => {
                setPayLink(e.target.value);
                setError(null);
                setSuccess(null);
              },
            }}
          />
        </div>
      </AddSection>

      <AddSection
        title="Social"
        hint="Optional public handles."
        icon={<FiShare2 className="h-4 w-4" />}
      >
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
          {(
            [
              ["whatsapp", "Whatsapp", whatsapp, setWhatsapp],
              ["facebook", "Facebook", facebook, setFacebook],
              ["twitter", "Twitter", twitter, setTwitter],
              ["instagram", "Instagram", instagram, setInstagram],
              ["youtube", "YouTube", youtube, setYoutube],
            ] as const
          ).map(([id, label, value, setter]) => (
            <InputField
              key={id}
              props={{
                id,
                title: label,
                type: "text",
                value: value || "",
                onChange: (e: any) => {
                  (setter as any)(e.target.value);
                  setError(null);
                  setSuccess(null);
                },
              }}
            />
          ))}
        </div>
      </AddSection>

      <AddSection
        title="Geo Position"
        hint="Helps map the embassy on travel guides."
        icon={<FiMapPin className="h-4 w-4" />}
      >
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          <InputField
            props={{
              id: "google_map_code",
              title: "Google Map Code",
              type: "text",
              value: googleMapCode || "",
              onChange: (e: any) => {
                setGoogleMapCode(e.target.value);
                setError(null);
                setSuccess(null);
              },
            }}
          />
          <InputField
            props={{
              id: "latitude",
              title: "Latitude",
              type: "text",
              value: latitude || "",
              onChange: (e: any) => {
                setLatitude(e.target.value);
                setError(null);
                setSuccess(null);
              },
            }}
          />
          <InputField
            props={{
              id: "longitude",
              title: "Longitude",
              type: "text",
              value: longitude || "",
              onChange: (e: any) => {
                setLongitude(e.target.value);
                setError(null);
                setSuccess(null);
              },
            }}
          />
        </div>
      </AddSection>

      <AddSection
        title="Visibility"
        hint="Where this entry shows up across the site."
        icon={<FiSettings className="h-4 w-4" />}
      >
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
          <Select
            props={{
              id: "embassy_status",
              title: "Status",
              option: ["visible", "invisible"],
              onChange: (e: any) => {
                if (e.target.selectedIndex === 0) setEmbassyStatus(1);
                else setEmbassyStatus(0);
                setError(null);
                setSuccess(null);
              },
            }}
          />
          <Select
            props={{
              id: "featured",
              title: "Featured",
              option: ["no", "yes"],
              onChange: (e: any) => {
                setFeatured(e.target.value);
                setError(null);
                setSuccess(null);
              },
            }}
          />
          <Select
            props={{
              id: "promoted",
              title: "Promoted",
              option: ["no", "yes"],
              onChange: (e: any) => {
                setPromoted(e.target.value);
                setError(null);
                setSuccess(null);
              },
            }}
          />
          <Select
            props={{
              id: "gallery",
              title: "Show in Gallery",
              option: ["no", "yes"],
              onChange: (e: any) => {
                const choice = e.target.value;
                setExpo(choice === "no" ? 0 : 1);
                setError(null);
                setSuccess(null);
              },
            }}
          />
        </div>
      </AddSection>

      <AddSection
        title="Description"
        hint="Hours, services and any travel notes."
        icon={<FiAlignLeft className="h-4 w-4" />}
        actions={
          <MyButton
            onClick={() => setShowDescriptionModal(true)}
            className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-br from-brand to-primary px-3 py-2 text-xs font-semibold text-white shadow hover:opacity-95"
          >
            <FaHandSparkles className="h-4 w-4" />
            Generate
          </MyButton>
        }
      >
        <TextArea
          props={{
            id: "description",
            title: "Description",
            type: "text",
            value: description ? description : "",
            onChange: (e: any) => {
              setDescription(e.target.value);
              setError(null);
              setSuccess(null);
            },
          }}
        />
      </AddSection>
    </AddPageShell>
  );
};

export default Add;
